import { Button, Table } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState, Dispatch } from 'react';
import { getAdminAppConfig, delAdminAppConfig } from '../../../api';
import { SystemMenuId } from '../../../model/system/menu';
import {
  CommonAction,
  ReduxCommonActionType,
} from '../../../store/modules/common';
import { IAPIAppConfigItem } from '../../../model/api-modules/app-config';
import { getColumns } from './lib/table';
import { ConfigModal } from './config-modal';
import { IAppConfigUpdateType, ICommonSupportPageProps } from '../../../model';
import { DelConfirmModal } from './del-confirm-modal';
import { ReloadOutlined } from '@ant-design/icons';
import { messageSuccess } from '../../../components/message';
import getFormatMessage from '../../../locales/get-format-message';
import { wrapRequestArea } from '../../../lib/http/lib';
import { IRootState } from '../../../store/reducers';

export interface ISelectOptionItem {
  readonly label: string;
  readonly value: Number;
}

export const AppConfig = (props: ICommonSupportPageProps) => {
  const { site } = props;
  const menuDispatch = useDispatch<Dispatch<CommonAction>>();
  const [loading, setLoading] = useState<boolean>(false);
  const [configList, setConfigList] = useState<
    ReadonlyArray<IAPIAppConfigItem>
  >([]);
  const [row, setRow] = useState<IAPIAppConfigItem | null>(null);
  const [editType, setEditType] = useState<IAppConfigUpdateType>();
  const [delRow, setDelRow] = useState<IAPIAppConfigItem | null>(null);
  const userInfo = useSelector((state: IRootState) => state.common.userInfo);

  menuDispatch({
    type: ReduxCommonActionType.SELECT_MENU_ID,
    result: SystemMenuId.APPConfig,
  });

  const getConfig = async () => {
    setLoading(true);
    const res = await wrapRequestArea(getAdminAppConfig, site);
    if (Array.isArray(res)) {
      setConfigList(res);
    }
    setLoading(false);
  };

  const onDel = (record: IAPIAppConfigItem) => {
    setDelRow(record);
  };

  const confirmDel = async () => {
    if (delRow) {
      const code = await delAdminAppConfig(delRow.id);
      if (code === 0) {
        messageSuccess(getFormatMessage('del_success'));
        getConfig();
        onClose(true);
        return true;
      }
    }
    return false;
  };

  const onAdd = () => {
    setEditType(IAppConfigUpdateType.Create);
  };

  const onEdit = (record: IAPIAppConfigItem) => {
    setRow(record);
    setEditType(IAppConfigUpdateType.Edit);
  };

  const onClose = (success?: boolean) => {
    setRow(null);
    setDelRow(null);
    setEditType(undefined);
    if (!!success) {
      getConfig();
    }
  };

  useEffect(() => {
    getConfig();
  }, []);

  const renderConfigModal = () => {
    if (editType) {
      const editRow = editType === IAppConfigUpdateType.Edit ? row : null;
      return <ConfigModal row={editRow} onClose={onClose} type={editType} />;
    }
    return null;
  };

  const renderDelConfirmModal = () => {
    if (delRow) {
      const { Domain: domain } = delRow;
      const textStyle = { color: 'var(--color-text-danger' };
      return (
        <DelConfirmModal
          title={getFormatMessage('del_service')}
          onOk={confirmDel}
          onClose={onClose}
          targetValue={domain}
        >
          <div>
            <h4 style={textStyle}>
              {getFormatMessage('del_confirm_h4', {
                params: {
                  domain,
                },
              })}
            </h4>
            <p>
              {getFormatMessage('del_confirm_desc_text1')}
              <span style={textStyle}>{domain}</span>
              {getFormatMessage('del_confirm_desc_text2')}
            </p>
          </div>
        </DelConfirmModal>
      );
    }
    return null;
  };

  return (
    <div>
      <div className="admin-page-title webkit-box flex-space-between">
        <div className="page-title-left">
          <span>{getFormatMessage('list')}</span>
          <ReloadOutlined onClick={getConfig} className="refresh-icon" />
        </div>
        <div className="page-title-right">
          <Button
            className="gc-btn gc-btn-primary btn-admin is-small"
            size="small"
            onClick={onAdd}
          >
            {getFormatMessage('new')}
          </Button>
        </div>
      </div>
      <Table
        rowKey="id"
        loading={loading}
        dataSource={configList}
        columns={getColumns(onEdit, onDel, userInfo)}
        pagination={false}
      />
      {renderConfigModal()}
      {renderDelConfirmModal()}
    </div>
  );
};
